/**
 * Handoff between the floor-plan reservation rail and the table-order screen.
 *
 * Seating a booking means opening an order on its table, which lives on another
 * route — so the intent is parked in localStorage the same way merge and transfer
 * already do it (`tableToMergeFrom`, `transferFromTable`). TableOrder picks it up
 * to pre-fill the table-setup dialog, then reports the order back.
 */
import { Reservation } from '../interfaces';

const KEY = 'tvSeatingReservation';

export interface SeatingHandoff {
    reservationId: string;
    code: string;
    tableId: string;
    partySize: number;
    guestName: string;
    guestEmail?: string;
    customerId?: string;
    prostayReservationId?: string;
    prostayReservationNumber?: string;
    prostayGuestId?: string;
    roomNumber?: string;
}

export function setSeatingHandoff(r: Reservation, tableId: string): void {
    const payload: SeatingHandoff = {
        reservationId: r.id,
        code: r.code,
        tableId,
        partySize: r.partySize,
        guestName: `${r.guest?.firstName || 'Guest'}${r.guest?.lastName ? ` ${r.guest.lastName}` : ''}`.trim(),
        guestEmail: r.guest?.email,
        customerId: r.guest.customerId,
        prostayReservationId: r.prostay?.reservationId,
        prostayReservationNumber: r.prostay?.reservationNumber,
        prostayGuestId: r.prostay?.guestId,
        roomNumber: r.prostay?.roomNumber,
    };
    try {
        localStorage.setItem(KEY, JSON.stringify(payload));
    } catch {
        /* storage full or blocked — seating still works, just without the pre-fill */
    }
}

/** Reads the handoff for a table, if one is pending. */
export function getSeatingHandoff(tableId?: string): SeatingHandoff | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SeatingHandoff;
        if (tableId && parsed.tableId !== tableId) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearSeatingHandoff(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
}
